// Scalar SQL function body prefix stripper (Slice 20 helper for indexBuilder).
//
// Scalar SQL functions (pure-SQL bodies — multi-statement procedural T-SQL /
// PL/SQL / PL/pgSQL / MySQL routines are covered separately by the four
// ANTLR procedural grammars in NAV3) ship as full `CREATE FUNCTION` source
// per dialect:
//   MSSQL:  `CREATE FUNCTION fn(...) RETURNS <type> AS [BEGIN] RETURN <expr> [END]`
//   PG:     `CREATE FUNCTION fn(...) RETURNS <scalar-type> AS $tag$ <select-or-expr> $tag$ LANGUAGE sql`
//   Oracle: `FUNCTION fn(...) RETURN <type> IS [DECLARE] BEGIN [BEGIN] ... END;`
//           (ALL_SOURCE returns the body without the leading `CREATE OR
//           REPLACE`). The wrapper stripper swallows the leading
//           `FUNCTION ... IS|AS` header plus every `BEGIN` keyword and every
//           block-terminating `END[;]` (control-structure terminators
//           `END IF;`/`END LOOP;`/`END CASE;` survive). The remaining text
//           is the inner statements; the universal scanner then splits on
//           `;` and the embedded SELECT lands as `kind === 'select'` for
//           Phase-2 walking. ProceduralBodyIndexing Slice 4.1.
//
// Other engines are no-ops:
//   MySQL  — functions are always procedural (covered by `MySqlRoutinesGrammar`).
//   SQLite — no user functions.
//
// `stripScalarFuncPrefix` returns the inner SELECT/expression source plus the
// byte offset to add back when re-deriving line/column ranges, mirroring the
// shape of `stripInlineTvfPrefix` in `inlineTvfPrefix.ts`.

import type { SqlEngine } from '../../sql-grammar/model';

export function stripScalarFuncPrefix(
  body: string,
  engine: SqlEngine,
): { selectSource: string; byteOffset: number } {
  if (engine === 'postgres') return stripPgDollarBody(body);
  if (engine === 'mssql') return stripMssqlScalarBody(body);
  if (engine === 'oracle') return stripOracleScalarBody(body);
  return { selectSource: body, byteOffset: 0 };
}

/** PG `pg_get_functiondef` always normalizes the body delimiter to `$function$`,
 *  but other engines / older dumps may use any `$tag$` (`$$`, `$body$`, …).
 *  Find the first `AS <tag>` and the matching close `<tag>`; return the slice
 *  in between. Same logic the inline-TVF stripper uses for PG. */
function stripPgDollarBody(body: string): { selectSource: string; byteOffset: number } {
  const m = /\bAS\s+(\$[A-Za-z0-9_]*\$)/i.exec(body);
  if (!m) return { selectSource: body, byteOffset: 0 };
  const tag = m[1];
  const innerStart = m.index + m[0].length;
  const closeIdx = body.indexOf(tag, innerStart);
  if (closeIdx < 0) return { selectSource: body, byteOffset: 0 };
  return { selectSource: body.slice(innerStart, closeIdx), byteOffset: innerStart };
}

/** MSSQL scalar functions have two parseable shapes:
 *    `CREATE FUNCTION fn(...) RETURNS <type> AS RETURN <expr>` (SQL 2019 inline)
 *    `CREATE FUNCTION fn(...) RETURNS <type> AS BEGIN RETURN <expr> END`
 *  We match `AS [BEGIN] RETURN` to handle both. Multi-statement procedural
 *  T-SQL bodies (DECLARE / SET / multiple statements before RETURN) are NAV3's
 *  job — they don't match this pattern and fall through to parse-failed.
 *
 *  After stripping the header, we replace any trailing `END` with spaces and
 *  unwrap any leading `(` / matching `)` — same pattern the inline-TVF stripper
 *  uses. The space-for-keyword swap preserves byte offsets so emitted ranges
 *  still align with the original `bodyText`. */
function stripMssqlScalarBody(body: string): { selectSource: string; byteOffset: number } {
  const m = /\bAS\s+(?:BEGIN\s+)?RETURN\s*/i.exec(body);
  if (!m) return { selectSource: body, byteOffset: 0 };
  const offset = m.index + m[0].length;
  const tail = body.slice(offset);
  const chars = tail.split('');

  // Replace any trailing `END` (with optional `;` and whitespace) with spaces.
  const endMatch = /\bEND\s*;?\s*$/i.exec(tail);
  if (endMatch) {
    for (let i = endMatch.index; i < endMatch.index + endMatch[0].length; i++) chars[i] = ' ';
  }

  // Unwrap a leading `(` and its matching `)` — converts `(SELECT …)` to
  // ` SELECT … ` so the parser sees a top-level SELECT statement.
  const leading = tail.match(/^\s*\(/);
  if (!leading) return { selectSource: chars.join(''), byteOffset: offset };
  const openIdx = leading[0].length - 1;
  const closeIdx = matchClosingParen(tail, openIdx);
  if (closeIdx < 0) return { selectSource: chars.join(''), byteOffset: offset };
  chars[openIdx] = ' ';
  chars[closeIdx] = ' ';
  return { selectSource: chars.join(''), byteOffset: offset };
}

/** Oracle PL/SQL scalar function body shape (from `ALL_SOURCE` per
 *  `oracleObjectBodyFetcher`):
 *    `FUNCTION name(...) RETURN <type> IS [v_x NUMBER;] BEGIN [DECLARE …
 *      BEGIN …; END;] …; END;`
 *  Strip the `FUNCTION … IS|AS` header plus every `BEGIN` keyword plus every
 *  block-terminating `END[;]`. Control-structure terminators
 *  (`END IF;` / `END LOOP;` / `END CASE;`) are excluded so they survive into
 *  the inner statements (the walker filters them out as `unknown` chunks).
 *  Replacement is space-for-keyword to preserve byte offsets so emitted
 *  ranges still align with the original `bodyText`. The universal scanner
 *  then splits the result on `;` and the embedded SELECT lands as
 *  `kind === 'select'`. */
function stripOracleScalarBody(body: string): { selectSource: string; byteOffset: number } {
  let s = body;
  // Strip the `(FUNCTION|PROCEDURE) name(...) ... (IS|AS)` header. Anchored
  // at start-of-body to avoid matching `AS` inside parameter literals.
  const headerRe = /^\s*(?:FUNCTION|PROCEDURE)\b[\s\S]*?\b(?:IS|AS)\b/i;
  const header = headerRe.exec(s);
  if (header) {
    const chars = s.split('');
    for (let i = 0; i < header.index + header[0].length; i++) {
      if (chars[i] !== '\n') chars[i] = ' ';
    }
    s = chars.join('');
  }
  s = s.replace(/\bBEGIN\b/gi, (m) => ' '.repeat(m.length));
  s = s.replace(
    /\bEND\b(?!\s+(?:IF|LOOP|CASE)\b)(?:\s+\w+)?\s*;/gi,
    (m) => ' '.repeat(m.length),
  );
  return { selectSource: s, byteOffset: 0 };
}

function matchClosingParen(body: string, openIdx: number): number {
  let depth = 1;
  for (let i = openIdx + 1; i < body.length; i++) {
    const c = body[i];
    if (c === '(') depth += 1;
    else if (c === ')') {
      depth -= 1;
      if (depth === 0) return i;
    } else if (c === "'") {
      i = body.indexOf("'", i + 1);
      if (i < 0) return -1;
    }
  }
  return -1;
}
